import React from "react";
import styled from "styled-components";
import {
  Button,
  Container,
  Dropdown,
  Grid,
  Icon,
  Image,
  Menu,
} from "semantic-ui-react";
import { NavLink } from "react-router-dom";

const Wrapper = styled.div`
  position: relative;
  z-index: 5;
  background-color: #ffffff;

  @media (max-width: 767px) {
    padding-top: 56px;
    -webkit-tap-highlight-color: transparent;
  }
`;

const LogoWrapper = styled.div`
  position: relative;
  margin: 2em 0;
`;

const Link = styled(NavLink)`
  color: #fff;
  font-weight: normal;
  font-size: 14px;
  display: block;
  &:hover {
    color: rgba(255, 255, 255, 0.75);
  }
`;

const DropdownLink = styled(NavLink)`
  color: #282828;
  font-size: 1.2em;
  display: block;
  padding: 0.7em 2em 0.7em 1em;
  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #282828;
  }
`;

const NavWrapper = styled.div`
  position: relative;
  color: #7d7c83;
  font-weight: bold;
  background: #002035;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);

  @media (max-width: 767px) {
    display: none;
  }
`;

const MobileTopBar = styled.div`
  display: none;

  @media (max-width: 767px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1001;
    display: flex;
    align-items: center;
    height: 56px;
    padding: 0 1rem;
    background: #002035;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  }
`;

const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  color: #fff;
  background: transparent;
  cursor: pointer;

  &:focus,
  &:active {
    outline: none;
    box-shadow: none;
  }
`;

const MobileOverlay = styled.div`
  display: none;

  @media (max-width: 767px) {
    position: fixed;
    top: 56px;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 999;
    display: ${({ open }) => (open ? "block" : "none")};
    background: rgba(0, 0, 0, 0.45);
  }
`;

const MobileDrawer = styled.aside`
  display: none;
  position: fixed;
  top: 56px;
  bottom: 0;
  left: 0;
  z-index: 1000;
  width: min(22rem, 88vw);
  overflow-y: auto;
  background: #fff;
  box-shadow: 4px 0 12px rgba(0, 0, 0, 0.2);
  transform: ${({ open }) => (open ? "translateX(0)" : "translateX(-105%)")};
  transition: transform 0.2s ease-in-out;

  @media (min-width: 768px) {
    display: none;
  }

  @media (max-width: 767px) {
    display: block;
  }
`;

const MobileMenu = styled.nav`
  padding: 0.5rem 0 1rem;
`;

const MobileLink = styled(NavLink)`
  display: block;
  padding: 0.9rem 1.25rem;
  color: #282828;
  font-size: 1rem;
  border-bottom: 1px solid #e5e5e5;

  &:hover,
  &.active {
    color: #002035;
    background: #f1f5f7;
  }
`;

const MobileAccordionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.9rem 1.25rem;
  border: 0;
  border-bottom: 1px solid #e5e5e5;
  color: #282828;
  background: #fff;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus {
    color: #002035;
    background: #f1f5f7;
  }
`;

const MobileSubmenu = styled.div`
  background: #f7f9fa;
`;

const MobileSubLink = styled(NavLink)`
  display: block;
  padding: 0.8rem 1.25rem 0.8rem 2rem;
  color: #282828;
  font-size: 0.95rem;
  border-bottom: 1px solid #e5e5e5;

  &:hover,
  &.active {
    color: #002035;
    background: #e8eef1;
  }
`;

const DonationButton = styled(Button)`
  &&& {
    background-color: #2f8f83;
    color: #fff;
  }

  &&&:hover {
    background-color: #26766c;
    color: #fff;
  }
`;

const DonationLink = styled(NavLink)`
  flex: 0 0 auto;
  margin-left: auto;
`;

const ContactLink = styled(NavLink)`
  @media (max-width: 767px) {
    display: none;
  }
`;

const MobileDonationButton = styled(DonationButton)`
  &&& {
    display: block;
    width: calc(100% - 2.5rem);
    margin: 1rem 1.25rem;
  }
`;

const Flex = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;

  & > .ui.menu {
    flex: 1 1 auto;
    min-width: max-content;
  }
`;

const DropdownMenu = ({ label, baseUrl, items = [] }) => (
  <Dropdown icon="angle down" item text={label}>
    <Dropdown.Menu>
      {items.map(({ label, path }, i) => (
        <Dropdown.Item key={i}>
          <DropdownLink to={`${baseUrl}/${path}`}>{label}</DropdownLink>
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  </Dropdown>
);

const LinkItem = ({ label, path, isVisible = true }) => {
  if (!isVisible) {
    return null;
  }

  return (
    <Menu.Item>
      <Link to={path}>{label}</Link>
    </Menu.Item>
  );
};

const Item = ({ items, path, ...rest }) => (
  <>
    {!items.length ? (
      <LinkItem path={path} {...rest} />
    ) : (
      <DropdownMenu items={items} baseUrl={path} {...rest} />
    )}
  </>
);

const MobileNavigation = ({ items, open, onToggle, onClose }) => {
  const [expandedPath, setExpandedPath] = React.useState(null);

  const toggle = (path) => {
    setExpandedPath((currentPath) => (currentPath === path ? null : path));
  };

  return (
    <>
      <MobileTopBar>
        <MobileMenuButton
          type="button"
          aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          aria-expanded={open}
          aria-controls="mobile-navigation-drawer"
          onClick={onToggle}
        >
          <Icon name={open ? "close" : "sidebar"} size="large" />
        </MobileMenuButton>
      </MobileTopBar>
      <MobileOverlay open={open} onClick={onClose} />
      <MobileDrawer
        id="mobile-navigation-drawer"
        aria-label="Navigation mobile"
        open={open}
      >
        <MobileMenu>
          {items.map(({ items: children = [], label, path }, index) =>
            children.length ? (
              <div key={path || index}>
                <MobileAccordionButton
                  type="button"
                  aria-expanded={expandedPath === path}
                  aria-controls={`mobile-submenu-${index}`}
                  onClick={() => toggle(path)}
                >
                  <span>{label}</span>
                  <Icon
                    name={expandedPath === path ? "angle up" : "angle down"}
                  />
                </MobileAccordionButton>
                {expandedPath === path && (
                  <MobileSubmenu id={`mobile-submenu-${index}`}>
                    {children.map((child) => (
                      <MobileSubLink
                        key={child.path}
                        to={`${path}/${child.path}`}
                        onClick={onClose}
                      >
                        {child.label}
                      </MobileSubLink>
                    ))}
                  </MobileSubmenu>
                )}
              </div>
            ) : (
              path === "/faire-un-don" ? (
                <NavLink key={path || index} to={path} onClick={onClose}>
                  <MobileDonationButton size="medium">
                    {label}
                  </MobileDonationButton>
                </NavLink>
              ) : (
                <MobileLink key={path || index} to={path} onClick={onClose}>
                  {label}
                </MobileLink>
              )
            )
          )}
        </MobileMenu>
      </MobileDrawer>
    </>
  );
};

const Navigation = ({ items = [] }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleMobileMenuToggle = () => setMobileMenuOpen((open) => !open);
  const handleMobileMenuClose = () => setMobileMenuOpen(false);

  return (
    <Wrapper>
      <MobileNavigation
        items={items}
        open={mobileMenuOpen}
        onToggle={handleMobileMenuToggle}
        onClose={handleMobileMenuClose}
      />
      <Container>
        <LogoWrapper>
          <Grid verticalAlign="middle">
            <Grid.Column width={8}>
              <Link to={{ pathname: "/" }} style={{ display: "inline" }}>
                <Image src="/images/transition-centre-sud.png" inline style={{width: 290, minWidth: 250}} />
              </Link>
            </Grid.Column>
            <Grid.Column textAlign="right" width={8}>
              <ContactLink to="/contact">
                <Button
                  size="large"
                  style={{ backgroundColor: "#607274", color: "#fff" }}
                >
                  Nous contacter
                </Button>
              </ContactLink>
            </Grid.Column>
          </Grid>
        </LogoWrapper>
      </Container>
      <NavWrapper>
        <Container>
          <Flex>
            <Menu position="right" borderless style={{ margin: 0 }}>
              {items.map(({ items = [], ...props }, i) => (
                <Item key={i} {...props} items={items} />
              ))}
            </Menu>
            <DonationLink to="/faire-un-don">
              <DonationButton size="medium">Faire un don</DonationButton>
            </DonationLink>
          </Flex>
        </Container>
      </NavWrapper>
    </Wrapper>
  );
};

export default Navigation;
